import { createSlice } from "@reduxjs/toolkit";
import { PostModel } from "./postSlice";

interface NewsState {
  data: PostModel[];
  activePage: number;
  loading: boolean;
  activeNews: PostModel | null;
  activeIndex: number;
  totalElements: number;
  totalPages: number;
  searchValue: string;
  searchNewsData: {
    data: {
      content: PostModel[];
      totalPages: number;
      totalElements: number;
    };
    currentPage: number;
  };
  saveNewsData: {
    data: {
      content: PostModel[];
      totalPages: number;
      totalElements: number;
    };
    currentPage: number;
  };
}

const initialPostDataState: NewsState = {
  data: [],
  activePage: 0,
  loading: false,
  activeNews: null,
  activeIndex: 0,
  totalElements: 0,
  totalPages: 0,
  searchValue: "",
  searchNewsData: {
    data: {
      content: [],
      totalPages: 0,
      totalElements: 0,
    },
    currentPage: 1,
  },
  saveNewsData: {
    data: {
      content: [],
      totalPages: 0,
      totalElements: 0,
    },
    currentPage: 1,
  },
};

export const newsSlice = createSlice({
  name: "newsData",
  initialState: initialPostDataState,
  reducers: {
    setNewsLoading: (state, action) => {
      state.loading = action.payload;
    },
    setNews: (state, action) => {
      state.data = action.payload.content;
      state.totalElements = action.payload.totalElements;
      state.totalPages = action.payload.totalPages;

      state.activePage = 0;
    },
    setNextPageNews: (state, action) => {
      const existingIds = new Set(state.data.map((item) => item.id));

      const uniqueNewItems = action.payload.content.filter(
        (item) => !existingIds.has(item.id),
      );

      state.data = [...state.data, ...uniqueNewItems];
      state.activePage = state.activePage + 1;
    },

    setFirstActiveNews: (state) => {
      state.activeNews = state.data[0] || null;
      state.activeIndex = 0;
      state.activePage = 0;
    },

    nextActiveNews: (state) => {
      if (state.activeIndex + 1 < state.data.length) {
        state.activeIndex += 1;
        state.activeNews = state.data[state.activeIndex];
      }
    },

    prevActiveNews: (state) => {
      if (state.activeIndex - 1 >= 0) {
        state.activeIndex -= 1;
        state.activeNews = state.data[state.activeIndex];
      }
    },
    setSearchValue: (state, action) => {
      state.searchValue = action.payload;
    },
    setSearchNewsData: (state, action) => {
      state.searchNewsData = action.payload;
    },
    setSearchNewsPage: (state, action) => {
      state.searchNewsData.currentPage = action.payload;
    },
    setSaveNewsData: (state, action) => {
      state.saveNewsData = action.payload;
    },
    setNewsById: (state, action) => {
      const exists = state.data.some(
        (item) => item.id === action.payload.data.id,
      );

      if (!exists) {
        state.data = [action.payload.data, ...state.data];
        state.totalElements += 1;
      }
    },
    resetNews: (state) => {
      state.data = [];
      state.activePage = 0;
      state.activeIndex = 0;
      state.activeNews = null;
      state.totalElements = 0;
      state.totalPages = 0;
    },
  },
});

export const {
  setNewsLoading,
  setNews,
  setNextPageNews,
  setFirstActiveNews,
  nextActiveNews,
  prevActiveNews,
  setSearchValue,
  setSearchNewsData,
  setSearchNewsPage,
  setSaveNewsData,
  setNewsById,
  resetNews,
} = newsSlice.actions;

export const selectActiveIndexNews = (state: { news: NewsState }) =>
  state.news.activeIndex;

export const selectNews = (state: { news: NewsState }) => state.news.data;

export const selectCurrentPageNews = (state: { news: NewsState }) =>
  state.news.activePage;
export const selectActiveNews = (state: { news: NewsState }) =>
  state.news.activeNews;
export const selectSearchValue = (state: { news: NewsState }) =>
  state.news.searchValue;
export const selectSearchNewsData = (state: { news: NewsState }) =>
  state.news.searchNewsData;
export const selectCurrentSearchPage = (state: { news: NewsState }) =>
  state.news.searchNewsData.currentPage;
export const selectSaveNewsData = (state: { news: NewsState }) =>
  state.news.saveNewsData;

export const selectTotalPages = (state: { news: NewsState }) =>
  state.news.totalPages;

export const selectNewsLoading = (state: { news: NewsState }) =>
  state.news.loading;

export default newsSlice.reducer;
